import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Button,
    Empty
} from "ncore-ui-kit";
import {
    InlineCode,
    DocsTable,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "StateCard";

const USAGE_CODE = `<Empty
    variant="card"
>
    <Empty.Header>
        <Empty.Media />
        <Empty.Title>
            No records yet
        </Empty.Title>
        <Empty.Description>
            New records you add will be listed here.
        </Empty.Description>
    </Empty.Header>
    <Empty.Content>
        <Button
            onPress={handleAddRecord}
            title="Add record"
        />
    </Empty.Content>
</Empty>`;

const RETRY_DELAY = 1400;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const StateCardPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        status,
        setStatus
    ] = useState<"error" | "loading" | "success">("error");

    useEffect(() => {
        if(status !== "loading") {
            return;
        }

        const timer = window.setTimeout(() => setStatus("success"), RETRY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        status
    ]);

    const isSuccess = status === "success";

    const handleRetry = () => setStatus("loading");

    const handleReset = () => setStatus("error");

    return <Article>
        <ComponentHeader
            lead={t("stateCardPage.lead")}
            name={NAME}
        />
        <Callout
            title={t("stateCardPage.noticeTitle")}
        >
            {t("stateCardPage.noticeText")}
        </Callout>
        <DocsTable
            rows={[
                [
                    <InlineCode>
                        {"title"}
                    </InlineCode>,
                    <InlineCode>
                        {"Empty.Title"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"subTitle"}
                    </InlineCode>,
                    <InlineCode>
                        {"Empty.Description"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"titleStyle, subTitleStyle"}
                    </InlineCode>,
                    <InlineCode>
                        {"Empty.Title style, Empty.Description style"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"icon, iconSize"}
                    </InlineCode>,
                    <InlineCode>
                        {"Empty.Media icon, iconSize"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"isShowIcon={false}"}
                    </InlineCode>,
                    t("stateCardPage.mapNoMedia")
                ],
                [
                    <InlineCode>
                        {"type, size, isLoading"}
                    </InlineCode>,
                    t("stateCardPage.mapSameProps")
                ],
                [
                    <InlineCode>
                        {"isTransparentBackground"}
                    </InlineCode>,
                    <InlineCode>
                        {"variant=\"plain\" | \"card\""}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"action"}
                    </InlineCode>,
                    t("stateCardPage.mapAction")
                ],
                [
                    <InlineCode>
                        {"style, role, aria-*"}
                    </InlineCode>,
                    t("stateCardPage.mapRoot")
                ]
            ]}
            headers={[
                t("stateCardPage.mapOld"),
                t("stateCardPage.mapNew")
            ]}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("stateCardPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("stateCardPage.overviewCaption")}
                isStacked={true}
            >
                <Empty
                    type={isSuccess ? "success" : "danger"}
                    isLoading={status === "loading"}
                    variant="card"
                >
                    <Empty.Header>
                        <Empty.Media />
                        <Empty.Title>
                            {isSuccess ? t("stateCardPage.connectedTitle") : t("stateCardPage.failedTitle")}
                        </Empty.Title>
                        <Empty.Description>
                            {isSuccess ? t("stateCardPage.connectedText") : t("stateCardPage.failedText")}
                        </Empty.Description>
                    </Empty.Header>
                    <Empty.Content>
                        <Button
                            title={isSuccess ? t("stateCardPage.startOver") : t("stateCardPage.retry")}
                            onPress={isSuccess ? handleReset : handleRetry}
                            type={isSuccess ? "success" : "danger"}
                        />
                    </Empty.Content>
                </Empty>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("stateCardPage.propsIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("stateCardPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("stateCardPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        type: "\"neutral\" | \"primary\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("stateCardPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("stateCardPage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("stateCardPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("stateCardPage.propIconSize"),
                        name: "iconSize",
                        type: "number"
                    },
                    {
                        description: t("stateCardPage.propIsShowIcon"),
                        defaultValue: "true",
                        name: "isShowIcon",
                        type: "boolean"
                    },
                    {
                        description: t("stateCardPage.propAction"),
                        type: "IButtonProps",
                        name: "action"
                    },
                    {
                        description: t("stateCardPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("stateCardPage.propIsTransparentBackground"),
                        name: "isTransparentBackground",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("stateCardPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("stateCardPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default StateCardPage;
